


// Guard against double execution: this file can be included by multiple
// slideshow-custom sections on the same page (e.g. once as the main
// homepage section and once inside the footer group), which caused
// SyntaxError: Identifier 'StatsCounter' has already been declared.
if (window.__slideshowCustomLoaded) {
  // no-op on subsequent executions
} else {
  window.__slideshowCustomLoaded = true;

class StatsCounter {
  constructor(element) {
    this.element = element;
    this.originalValue = element.dataset.value;
    this.duration = parseInt(element.dataset.duration) || 2000;

    // Animate only genuine numeric metrics. Text such as
    // "14-day cooling-off period" must render immediately and never count up.
    // Supported examples: 100, 100+, 75%, +500 and € 25,50.
    const match = this.originalValue.match(/^([^0-9]*?)([\d.,]+)\s*([+%]?)$/);

    if (match) {
      this.prefix = match[1] || '';
      const numStr = match[2];
      this.suffix = match[3] || '';

      // Normalize number string (replace , with . for parsing)
      // If multiple dots/commas, simplistic approach: remove all but last separator if it looks like decimal
      // For robustness: just replace ',' with '.'
      const normalizedNum = numStr.replace(/,/g, '.');
      this.target = parseFloat(normalizedNum);

      // Determine decimal places from original string
      this.isDecimal = numStr.includes(',') || numStr.includes('.');
      if (this.isDecimal) {
        const parts = numStr.split(/[.,]/);
        this.decimals = parts[parts.length - 1].length;
      } else {
        this.decimals = 0;
      }
    } else {
      this.target = null;
      this.prefix = '';
      this.suffix = '';
    }

    this.animated = false;
    this.startValue = 0;

    if (this.target === null || isNaN(this.target)) {
      this.element.textContent = this.originalValue;
      return;
    }

    this.init();
  }

  init() {
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach((/** @type {IntersectionObserverEntry} */ entry) => {
          if (entry.isIntersecting && !this.animated) {
            this.animate();
            this.observer?.unobserve(entry.target);
          }
        });
      }, { threshold: 0.2 });
      if (this.element) this.observer.observe(this.element);
    } else {
      this.animate();
    }
  }

  animate() {
    this.animated = true;
    this.startTime = null;
    requestAnimationFrame((/** @type {number} */ timestamp) => this.update(timestamp));
  }

  /** @param {number} now */
  update(now) {
    if (!this.startTime) this.startTime = now;
    const elapsed = now - this.startTime;
    const progress = Math.min(elapsed / this.duration, 1);

    // Ease out cubic
    const easeOut = 1 - Math.pow(1 - progress, 3);

    const current = this.startValue + (this.target - this.startValue) * easeOut;

    this.element.textContent = this.formatNumber(current);

    if (progress < 1) {
      requestAnimationFrame((timestamp) => this.update(timestamp));
    } else {
      this.element.textContent = this.formatNumber(this.target);
    }
  }

  formatNumber(num) {
    let formatted;
    if (this.isDecimal) {
      formatted = num.toFixed(this.decimals).replace('.', ',');
    } else {
      formatted = Math.floor(num).toLocaleString('nl-NL');
    }
    return `${this.prefix}${formatted}${this.suffix}`;
  }
}

class JoinMovementCounter {
  constructor(element) {
    this.element = element;
    const value = element.dataset.value || '';
    const trimmedValue = value.toString().trim();

    const numberPattern = /^-?\d+([.,]\d+)?$/;
    const isNumber = numberPattern.test(trimmedValue);

    if (isNumber) {
      const cleanedValue = trimmedValue.replace(/,/g, '.');
      const targetValue = parseFloat(cleanedValue);

      if (!isNaN(targetValue) && isFinite(targetValue)) {
        this.target = targetValue;
        this.isDecimal = targetValue % 1 !== 0;
        this.decimalPlaces = this.isDecimal ? (cleanedValue.toString().split('.')[1] || '').length : 0;
        this.duration = parseInt(element.dataset.duration) || 2000;
        this.startValue = 0;
        this.startTime = null;
        this.animated = false;
        this.observer = null;
        this.isNumber = true;
        this.init();
        return;
      }
    }

    this.isNumber = false;
    element.textContent = value;
    element.setAttribute('data-is-number', 'false');
  }

  init() {
    if (!this.isNumber) return;

    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((/** @type {IntersectionObserverEntry} */ entry) => {
            if (entry.isIntersecting && !this.animated) {
              this.animate();
              this.observer?.unobserve(entry.target);
            }
          });
        },
        {
          threshold: 0.5,
          rootMargin: '0px'
        }
      );
      if (this.element) this.observer.observe(this.element);
    } else {
      this.animate();
    }
  }

  animate() {
    if (this.animated || !this.isNumber) return;
    this.animated = true;
    this.startTime = performance.now();
    this.update();
  }

  update() {
    if (!this.isNumber) return;

    const now = performance.now();
    const elapsed = now - this.startTime;
    const progress = Math.min(elapsed / this.duration, 1);

    const easeOut = 1 - Math.pow(1 - progress, 3);
    let currentValue = this.startValue + (this.target - this.startValue) * easeOut;

    if (this.isDecimal) {
      currentValue = parseFloat(currentValue.toFixed(this.decimalPlaces));
    } else {
      currentValue = Math.floor(currentValue);
    }

    this.element.textContent = this.formatNumber(currentValue);

    if (progress < 1) {
      requestAnimationFrame(() => this.update());
    } else {
      this.element.textContent = this.formatNumber(this.target);
    }
  }

  formatNumber(num) {
    if (this.isDecimal) {
      return num.toFixed(this.decimalPlaces).replace('.', ',');
    } else {
      return num.toLocaleString('nl-NL');
    }
  }
}

class JoinMovementSection {
  constructor(sectionId) {
    this.sectionId = sectionId;
    this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
    if (!this.section) return;

    this.tabs = this.section.querySelectorAll('.join-movement-section__tab');
    this.statsCards = this.section.querySelectorAll('.join-movement-section__card');
    this.sliderPosts = this.section.querySelectorAll('.join-movement-section__slider-slide');
    this.activeCategory = 'all';

    this.init();
  }

  init() {
    // Tab switching
    this.tabs.forEach((/** @type {HTMLElement} */ tab) => {
      tab.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        let categoryId = tab.dataset.tabCategory || '';
        // If category is empty, undefined, or looks like an index (just a number), treat as 'all'
        if (!categoryId || categoryId === '' || /^\d+$/.test(categoryId)) {
          categoryId = 'all';
        }
        this.switchTab(categoryId, tab);
      });
    });

    // Initialize slider if posts exist
    if (this.sliderPosts.length > 0) {
      this.initSlider();
    }
  }

  switchTab(categoryId, clickedTab) {
    this.activeCategory = categoryId;

    // Update tab styles
    const activeTabBg = this.section.dataset.activeTabBg || '';
    const activeTabColor = this.section.dataset.activeTabColor || '';
    const inactiveTabBg = this.section.dataset.inactiveTabBg || '';
    const inactiveTabColor = this.section.dataset.inactiveTabColor || '';
    const inactiveTabBorder = this.section.dataset.inactiveTabBorder || '';

    this.tabs.forEach((tab) => {
      const isActive = tab === clickedTab;
      tab.classList.toggle('join-movement-section__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive.toString());

      if (isActive) {
        tab.style.backgroundColor = activeTabBg || '#000';
        tab.style.color = activeTabColor || '#fff';
        tab.style.borderColor = 'transparent';
      } else {
        tab.style.backgroundColor = inactiveTabBg || 'transparent';
        tab.style.color = inactiveTabColor || '#000';
        tab.style.borderColor = inactiveTabBorder || '#e0e0e0';
      }
    });

    // Filter stats cards - only hide individual cards, not the container
    this.statsCards.forEach((card) => {
      const cardCategory = card.dataset.category || '';
      // Normalize: treat empty, 'all', or numeric-only as 'all'
      let normalizedCardCategory = 'all';
      if (cardCategory && cardCategory !== 'all' && cardCategory !== '' && !/^\d+$/.test(cardCategory)) {
        normalizedCardCategory = cardCategory;
      }

      // Show card if: tab is 'all', or card is 'all', or categories match exactly
      const shouldShow = categoryId === 'all' || normalizedCardCategory === 'all' || normalizedCardCategory === categoryId;

      if (shouldShow) {
        card.removeAttribute('hidden');
        card.style.display = '';
        card.style.visibility = 'visible';
      } else {
        card.setAttribute('hidden', '');
        card.style.display = 'none';
      }
    });

    // Filter slider posts - only hide individual posts, not the container
    this.sliderPosts.forEach((post) => {
      const postCategory = post.dataset.category || '';
      // Normalize: treat empty, 'all', or numeric-only as 'all'
      let normalizedPostCategory = 'all';
      if (postCategory && postCategory !== 'all' && postCategory !== '' && !/^\d+$/.test(postCategory)) {
        normalizedPostCategory = postCategory;
      }

      // Show post if: tab is 'all', or post is 'all', or categories match exactly
      const shouldShow = categoryId === 'all' || normalizedPostCategory === 'all' || normalizedPostCategory === categoryId;

      if (shouldShow) {
        post.removeAttribute('hidden');
        post.style.display = '';
        post.style.visibility = 'visible';
      } else {
        post.setAttribute('hidden', '');
        post.style.display = 'none';
      }
    });

    // Ensure containers and other elements remain visible
    const cardsContainer = this.section.querySelector('.join-movement-section__cards');
    if (cardsContainer) {
      cardsContainer.removeAttribute('hidden');
      cardsContainer.style.display = '';
      cardsContainer.style.visibility = 'visible';
    }

    const sliderWrapper = this.section.querySelector('.join-movement-section__slider-wrapper');
    if (sliderWrapper) {
      sliderWrapper.removeAttribute('hidden');
      sliderWrapper.style.display = '';
      sliderWrapper.style.visibility = 'visible';
    }

    const slider = this.section.querySelector('.join-movement-section__slider');
    if (slider) {
      slider.removeAttribute('hidden');
      slider.style.display = '';
      slider.style.visibility = 'visible';
    }

    const sliderTrack = this.section.querySelector('.join-movement-section__slider-track');
    if (sliderTrack) {
      sliderTrack.removeAttribute('hidden');
      sliderTrack.style.display = '';
      sliderTrack.style.visibility = 'visible';
    }

    const buttonsContainer = this.section.querySelector('.join-movement-section__buttons');
    if (buttonsContainer) {
      buttonsContainer.removeAttribute('hidden');
      buttonsContainer.style.display = '';
      buttonsContainer.style.visibility = 'visible';
    }

    const avatarsContainer = this.section.querySelector('.join-movement-section__avatars');
    if (avatarsContainer) {
      avatarsContainer.removeAttribute('hidden');
      avatarsContainer.style.display = '';
      avatarsContainer.style.visibility = 'visible';
    }

    // Reset slider position and reinitialize if needed
    if (this.slider) {
      // Reinitialize slider to account for hidden slides
      setTimeout(() => {
        if (this.slider && this.slider.slider) {
          const visibleSlides = /** @type {HTMLElement[]} */ (Array.from(this.slider.slider.querySelectorAll('.join-movement-section__slider-slide:not([hidden])')));
          this.slider.slides = visibleSlides;
          this.slider.totalSlides = visibleSlides.length;

          // Ensure slider track is visible
          if (this.slider.track) {
            this.slider.track.removeAttribute('hidden');
            this.slider.track.style.display = '';
            this.slider.track.style.visibility = 'visible';
          }

          // Only reset position if there are visible slides
          if (visibleSlides.length > 0) {
            this.slider.goToSlide(0);
            // Restart autoplay after filtering
            if (this.slider.startAutoplay) {
              this.slider.startAutoplay();
            }
          } else {
            // If no visible slides, still keep slider visible but reset position
            if (this.slider.track) {
              this.slider.track.style.transform = 'translateX(0px)';
            }
          }
        }
      }, 150);
    } else if (this.sliderPosts.length > 0) {
      // If slider not initialized yet, initialize it
      this.initSlider();
    }
  }

  initSlider() {
    const sliderId = this.section.querySelector('[data-slider]')?.dataset.slider;
    if (!sliderId) return;

    this.slider = new JoinMovementSlider(sliderId);
  }
}

class JoinMovementSlider {
  constructor(sliderId) {
    /** @type {string} */
    this.sliderId = sliderId;
    /** @type {HTMLElement | null} */
    this.slider = document.querySelector(`[data-slider="${sliderId}"]`);
    /** @type {HTMLElement | null} */
    this.track = null;
    /** @type {HTMLElement[]} */
    this.slides = [];
    /** @type {number} */
    this.totalSlides = 0;
    /** @type {number} */
    this.currentIndex = 0;
    /** @type {number} */
    this.slidesPerView = 3;
    /** @type {any} */
    this.autoplayInterval = null;
    /** @type {number} */
    this.autoplaySpeed = 4000;
    /** @type {boolean} */
    this.isPaused = false;
    /** @type {HTMLElement | null} */
    this.prevButton = null;
    /** @type {HTMLElement | null} */
    this.nextButton = null;

    if (!this.slider) return;

    this.track = this.slider.querySelector('.join-movement-section__slider-track');
    this.slides = /** @type {HTMLElement[]} */ (Array.from(this.slider.querySelectorAll('.join-movement-section__slider-slide:not([hidden])')));

    this.slidesPerView = this.getSlidesPerView();
    // satisfying compiler check for this.slides
    const slidesArr = /** @type {HTMLElement[]} */ (this.slides || []);
    this.totalSlides = slidesArr.length;

    this.init();
  }

  getSlidesPerView() {
    if (window.innerWidth <= 749) return 1;
    if (window.innerWidth <= 989) return 2;
    return 3;
  }

  init() {
    if (!this.track || !this.slides || this.slides.length === 0) return;

    // Navigation buttons
    if (this.track && this.prevButton) {
      this.prevButton.addEventListener('click', () => this.prev());
    }
    if (this.track && this.nextButton) {
      this.nextButton.addEventListener('click', () => this.next());
    }

    // Pause autoplay on hover
    if (this.slider) {
      this.slider.addEventListener('mouseenter', () => {
        this.pauseAutoplay();
      });
      this.slider.addEventListener('mouseleave', () => {
        this.resumeAutoplay();
      });
    }

    // Touch/swipe support
    let startX = 0;
    let currentX = 0;
    let isDragging = false;

    this.track.addEventListener('touchstart', (/** @type {TouchEvent} */ e) => {
      if (e.touches.length === 0) return;
      startX = e.touches[0].clientX;
      isDragging = true;
      this.pauseAutoplay(); // Pause on touch
    });

    this.track.addEventListener('touchmove', (/** @type {TouchEvent} */ e) => {
      if (!isDragging || e.touches.length === 0) return;
      currentX = e.touches[0].clientX;
    });

    this.track.addEventListener('touchend', () => {
      if (!isDragging) return;
      isDragging = false;
      const diff = startX - currentX;
      if (Math.abs(diff) > 50) {
        if (diff > 0) {
          this.next();
        } else {
          this.prev();
        }
      }
      // Resume autoplay after touch ends
      setTimeout(() => {
        this.resumeAutoplay();
      }, 2000); // Resume after 2 seconds
    });

    // Resize handler
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.slidesPerView = this.getSlidesPerView();
        this.updateSlides();
        this.goToSlide(Number(this.currentIndex));
        // Restart autoplay after resize
        this.startAutoplay();
      }, 250);
    });

    this.updateSlides();
    this.updateButtons();

    // Start autoplay
    this.startAutoplay();
  }

  startAutoplay() {
    this.stopAutoplay();
    if (this.totalSlides <= this.slidesPerView) return; // Don't autoplay if all slides are visible

    this.autoplayInterval = setInterval(() => {
      if (!this.isPaused) {
        this.next();
      }
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  pauseAutoplay() {
    this.isPaused = true;
  }

  resumeAutoplay() {
    this.isPaused = false;
  }

  updateSlides() {
    if (!this.slider) return;
    // Filter out hidden slides
    this.slides = /** @type {HTMLElement[]} */ (Array.from(this.slider.querySelectorAll('.join-movement-section__slider-slide:not([hidden])')));
    this.totalSlides = this.slides.length;
  }

  prev() {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    } else {
      this.currentIndex = Math.max(0, this.totalSlides - this.slidesPerView);
    }
    this.goToSlide(Number(this.currentIndex));
  }

  next() {
    const maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);
    if (this.currentIndex < maxIndex) {
      this.currentIndex++;
    } else {
      this.currentIndex = 0;
    }
    this.goToSlide(Number(this.currentIndex));
  }

  /** @param {number} index */
  goToSlide(index) {
    this.updateSlides();
    if (this.slides.length === 0) return;

    this.currentIndex = Math.max(0, Math.min(index, Math.max(0, this.totalSlides - this.slidesPerView)));

    if (!this.track) return;

    const slideWidth = this.slides[0]?.offsetWidth || 0;
    const gap = 32; // 2rem gap
    const translateX = -(this.currentIndex * (slideWidth + gap));

    this.track.style.transform = `translateX(${translateX}px)`;
    this.updateButtons();
  }

  updateButtons() {
    const maxIndex = Math.max(0, this.totalSlides - this.slidesPerView);

    if (this.prevButton instanceof HTMLButtonElement) {
      this.prevButton.disabled = this.currentIndex === 0 && this.totalSlides <= this.slidesPerView;
    }
    if (this.nextButton instanceof HTMLButtonElement) {
      this.nextButton.disabled = this.currentIndex >= maxIndex && this.totalSlides <= this.slidesPerView;
    }
  }
}



class CollectionTabsSection {
  /** @param {string} blockId */
  constructor(blockId) {
    this.blockId = blockId;
    this.section = document.querySelector(`[data-block-id="${blockId}"]`);
    if (!this.section) return;

    this.tabs = /** @type {HTMLElement[]} */ (Array.from(this.section.querySelectorAll('.collection-tabs-section__tab')));
    this.sliders = /** @type {HTMLElement[]} */ (Array.from(this.section.querySelectorAll('.collection-tabs-section__slider')));
    /** @type {number} */
    this.activeTabIndex = 0;

    this.init();
  }

  init() {
    // Tab switching
    this.tabs.forEach((/** @type {HTMLElement} */ tab, index) => {
      tab.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        const tabIndex = parseInt(tab.dataset.tabIndex || '0', 10);
        this.switchTab(tabIndex);
      });
    });

    // Initialize sliders
    (this.sliders || []).forEach((/** @type {HTMLElement} */ slider) => {
      this.initSlider(slider);
    });

    // Listen for Add to Cart events
    this.section.addEventListener('cart:update', (/** @type {any} */ event) => {
      const detail = event.detail;
      // Check if it's an add action from our component
      if (detail && detail.data && detail.data.source === 'product-form-component') {
        const productTitle = event.target?.dataset?.productTitle || 'Product';
        this.showToast(productTitle);
      }
    });

  }

  /** @param {string} productTitle */
  showToast(productTitle) {
    const toast = document.getElementById('ls-atc-toast');
    const titleElement = document.getElementById('toast-product-title');

    if (toast && titleElement) {
      titleElement.textContent = productTitle;
      toast.classList.add('is-active');

      // Handle "View cart" click to open drawer
      const viewCartBtn = toast.querySelector('[data-open-cart-drawer]');
      if (viewCartBtn && !viewCartBtn.dataset.listenerAdded) {
        viewCartBtn.addEventListener('click', (e) => {
          e.preventDefault();
          const cartDrawer = document.querySelector('cart-drawer-component');
          if (cartDrawer) {
            // Close toast first
            toast.classList.remove('is-active');

            // Open drawer
            if (typeof cartDrawer.open === 'function') {
              cartDrawer.open();
            } else if (typeof cartDrawer.showDialog === 'function') {
              cartDrawer.showDialog();
            } else {
              // Fallback to URL
              window.location.href = viewCartBtn.href;
            }
          } else {
            // Fallback to URL
            window.location.href = viewCartBtn.href;
          }
        });

        viewCartBtn.dataset.listenerAdded = 'true';
      }

      // Auto-close after 3 seconds
      if (this.toastTimeout) clearTimeout(this.toastTimeout);
      this.toastTimeout = setTimeout(() => {
        toast.classList.remove('is-active');
      }, 3000);
    }
  }

  // Lightbox functionality removed - no longer needed with product cards

  /** @param {number} tabIndex */
  switchTab(tabIndex) {
    // Prevent switching to the same tab
    if (tabIndex === this.activeTabIndex) return;

    // Get active tab styles from the first active tab or use defaults
    const firstActiveTab = /** @type {HTMLElement | null} */ (this.section.querySelector('.collection-tabs-section__tab--active'));
    const activeTabBg = firstActiveTab?.style.backgroundColor || '#000000';
    const activeTabColor = firstActiveTab?.style.color || '#ffffff';
    const inactiveTabColor = '#000000';
    const inactiveTabBorder = '#e0e0e0';

    // Get block settings if available
    const blockElement = this.section.closest('[data-block-id]');
    if (blockElement && firstActiveTab) {
      // Try to get from data attributes or computed styles
      const computedStyle = window.getComputedStyle(firstActiveTab || this.tabs[0]);
    }

    // Update tabs with smooth transition
    this.tabs.forEach((/** @type {Element} */ tabEL, index) => {
      const tab = /** @type {HTMLElement} */ (tabEL);
      const isActive = index === tabIndex;
      tab.classList.toggle('collection-tabs-section__tab--active', isActive);
      tab.setAttribute('aria-selected', isActive.toString());

      // Update styles
      if (isActive) {
        tab.style.backgroundColor = activeTabBg;
        tab.style.color = activeTabColor;
        tab.style.borderColor = 'transparent';
      } else {
        tab.style.backgroundColor = 'transparent';
        tab.style.color = inactiveTabColor;
        tab.style.borderColor = inactiveTabBorder;
      }
    });

    // Smooth fade transition for sliders
    const currentSlider = /** @type {HTMLElement | undefined} */ (Array.from(this.sliders)[this.activeTabIndex]);
    const nextSlider = /** @type {HTMLElement | undefined} */ (Array.from(this.sliders)[tabIndex]);

    if (currentSlider && nextSlider) {
      // Fade out current slider
      currentSlider.style.opacity = '0';
      currentSlider.style.transition = 'opacity 0.4s ease-in-out';

      setTimeout(() => {
        // Hide current slider
        currentSlider.classList.remove('collection-tabs-section__slider--active');
        currentSlider.setAttribute('hidden', '');

        // Show and fade in next slider
        nextSlider.removeAttribute('hidden');
        nextSlider.classList.add('collection-tabs-section__slider--active');
        nextSlider.style.opacity = '0';
        nextSlider.style.transition = 'opacity 0.4s ease-in-out';

        // Force reflow
        nextSlider.offsetHeight;

        // Fade in
        setTimeout(() => {
          nextSlider.style.opacity = '1';
        }, 10);

        // Reset slider position when switching tabs
        const sliderInstance = nextSlider.dataset.sliderInstance;
        if (sliderInstance && window[sliderInstance]) {
          window[sliderInstance].goToSlide(0);
        }
      }, 400);
    } else {
      // Fallback for first load or if sliders don't exist
      (this.sliders || []).forEach((sliderEL, index) => {
        const slider = /** @type {HTMLElement} */ (sliderEL);
        const isActive = index === tabIndex;
        slider.classList.toggle('collection-tabs-section__slider--active', isActive);
        if (isActive) {
          slider.removeAttribute('hidden');
          slider.style.opacity = '1';
          // Reset slider position when switching tabs
          const sliderInstance = slider.dataset.sliderInstance;
          if (sliderInstance && (/** @type {any} */ (window))[sliderInstance]) {
            (/** @type {any} */ (window))[sliderInstance].goToSlide(0);
          }
        } else {
          slider.setAttribute('hidden', '');
          slider.style.opacity = '0';
        }
      });
    }

    this.activeTabIndex = tabIndex;
  }

  /** @param {HTMLElement} slider */
  initSlider(slider) {
    // NATIVE SCROLL FIX: Removed JS-based transforms and touch inhibitors to allow CSS Scroll Snap
    const sliderId = `collection-tabs-slider-${this.blockId}-${slider.dataset.tabIndex}`;
    slider.dataset.sliderId = sliderId;

    const track = /** @type {HTMLElement | null} */ (slider.querySelector('.collection-tabs-section__slider-track'));
    const slidesContainer = /** @type {HTMLElement | null} */ (slider.querySelector('.collection-tabs-section__slider-slides'));
    if (!track || !slidesContainer) return;

    const slides = /** @type {HTMLElement[]} */ (Array.from(slidesContainer.querySelectorAll('.collection-tabs-section__product-card, .collection-tabs-section__image-card')));
    const dotsContainer = /** @type {HTMLElement | null} */ (slider.querySelector('.collection-tabs-section__slider-dots'));

    if (slides.length === 0) return;

    // Force styles to ensure native scroll works if CSS missed it
    track.style.overflowX = 'auto';
    track.style.scrollSnapType = 'x mandatory';
    slidesContainer.style.transform = 'none';
    slidesContainer.style.display = 'flex';

    // Generate Navigation Dots (Visual Only - Native Scroll drives the movement)
    if (dotsContainer && slides.length > 0) {
      // Mobile: 1 dot per slide. Desktop: 1 dot per view (approx)
      let dotsCount = slides.length;
      if (window.innerWidth >= 990) {
        dotsCount = Math.ceil(slides.length / 3);
      }

      dotsContainer.innerHTML = '';
      for (let i = 0; i < dotsCount; i++) {
        const dot = document.createElement('button');
        dot.className = 'collection-tabs-section__slider-dot';
        if (i === 0) dot.classList.add('collection-tabs-section__slider-dot--active');
        dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
        dot.dataset.dotIndex = i;

        // Click to scroll (Smooth)
        dot.addEventListener('click', () => {
          const targetSlide = /** @type {HTMLElement | undefined} */ (slides[i * (window.innerWidth >= 990 ? 3 : 1)]);
          if (targetSlide) {
            targetSlide.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });

            // Update active dot immediately for better feedback
            const allDots = dotsContainer.querySelectorAll('.collection-tabs-section__slider-dot');
            allDots.forEach(dEL => {
              const d = /** @type {HTMLElement} */ (dEL);
              d.classList.remove('collection-tabs-section__slider-dot--active');
            });
            dot.classList.add('collection-tabs-section__slider-dot--active');
          }
        });
        dotsContainer.appendChild(dot);
      }

      // Optional: Simple Scroll Observer to update dots
      const updateDots = () => {
        if (!track) return;
        const scrollLeft = track.scrollLeft;
        const width = track.offsetWidth;
        const index = Math.round(scrollLeft / width);

        const allDots = dotsContainer.querySelectorAll('.collection-tabs-section__slider-dot');
        allDots.forEach((d, idx) => {
          d.classList.toggle('collection-tabs-section__slider-dot--active', idx === index);
        });
      };
      track.addEventListener('scroll', () => {
        // Debounce slightly or just run
        requestAnimationFrame(updateDots);
      }, { passive: true });
    }
  }

  getSlidesPerView() {
    if (window.innerWidth <= 749) return 1;
    if (window.innerWidth <= 989) return 2;
    return 4;
  }
}

// Initialize dots click handlers


document.addEventListener('DOMContentLoaded', () => {
  // Initialize Stats Counters
  const statsCounters = document.querySelectorAll('.stats-block__number[data-value]');
  statsCounters.forEach((counter) => {
    new StatsCounter(counter);
  });

  // Initialize join movement counters
  const joinCounters = document.querySelectorAll('.join-movement-section__number[data-value]');
  joinCounters.forEach((counter) => {
    new JoinMovementCounter(counter);
  });

  // Initialize Slideshow New
  const slideshows = document.querySelectorAll('.slideshow-new-wrapper');
  slideshows.forEach((slideshow) => {
    new SlideshowNew(slideshow);
  });

  // Initialize Bundle sliders
  const bundleSliders = document.querySelectorAll('.ls-bundle-slider');
  bundleSliders.forEach((slider) => {
    new BundleSlider(slider);
  });





  // Initialize How We Help sections (New)
  const howWeHelpNewBlocks = document.querySelectorAll('.how-we-help-new');
  howWeHelpNewBlocks.forEach((block) => {
    new HowWeHelpNew(block);
  });

  // Initialize How We Help sections (Legacy - keep if needed or remove if strictly replacing)
  const howWeHelpSections = document.querySelectorAll('.how-we-help-section[data-section-id]');
  howWeHelpSections.forEach((sectionEL) => {
    const section = /** @type {HTMLElement} */ (sectionEL);
    const sectionId = section.dataset.sectionId;
    if (sectionId && (/** @type {any} */ (window))['HowWeHelpSection']) { // Check if class exists
      new ((/** @type {any} */ (window))['HowWeHelpSection'])(sectionId);
    }
  });

  // Initialize Collection Tabs sections
  const collectionTabsSections = document.querySelectorAll('.collection-tabs-section[data-block-id]');
  collectionTabsSections.forEach((/** @type {HTMLElement} */ section) => {
    const blockId = section.dataset.blockId;
    if (blockId) new CollectionTabsSection(blockId);
  });

  // Initialize Join Movement sections
  const joinMovementSections = document.querySelectorAll('.join-movement-section[data-section-id]');
  joinMovementSections.forEach((/** @type {HTMLElement} */ section) => {
    const sectionId = section.dataset.sectionId;
    if (sectionId) new JoinMovementSection(sectionId);
  });

  // Smooth scroll for hero slide links
  /* ---------- Slideshow New: prevent slider from blocking hero link ---------- */
  document.addEventListener('click', function (e) {
    const links = document.querySelectorAll('.slideshow-new .hero-slide-link');
    if (links.length === 0) return;

    for (const link of links) {
      const r = link.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue; // Skip hidden links

      const x = e.clientX;
      const y = e.clientY;

      const inside = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      if (inside) {
        e.preventDefault();
        e.stopPropagation();

        const target = document.getElementById('collections-tab');
        if (target) {
          const headerOffset = 80; // sticky header offset
          const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        return;
      }
    }
  }, true);

  document.addEventListener('touchend', function (e) {
    const links = document.querySelectorAll('.slideshow-new .hero-slide-link');
    if (links.length === 0) return;
    if (!e.changedTouches || !e.changedTouches[0]) return;

    for (const link of links) {
      const r = link.getBoundingClientRect();
      if (r.width === 0 || r.height === 0) continue; // Skip hidden links

      const t = e.changedTouches[0];
      const x = t.clientX;
      const y = t.clientY;

      const inside = x >= r.left && x <= r.right && y >= r.top && y <= r.bottom;
      if (inside) {
        e.preventDefault();
        e.stopPropagation();

        const target = document.getElementById('collections-tab');
        if (target) {
          const headerOffset = 80;
          const top = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
          window.scrollTo({ top, behavior: 'smooth' });
        }
        return;
      }
    }
  }, { capture: true, passive: false });
  /* ------------------------------------------------------------------------ */
});

if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (/** @type {any} */ event) => {
    if (event.detail.sectionId) {
      const section = document.querySelector(`[data-section-id="${event.detail.sectionId}"]`);
      if (section) {
        // Re-initialize stats counters
        const counters = section.querySelectorAll('.stats-block__number[data-value]');
        counters.forEach((counter) => {
          new StatsCounter(counter);
        });



        // Re-initialize join movement counters
        const joinCounters = section.querySelectorAll('.join-movement-section__number[data-value]');
        joinCounters.forEach((counter) => {
          new JoinMovementCounter(counter);
        });

        // Re-initialize dots


        // Re-initialize Slideshow New
        const slideshows = section.querySelectorAll('.slideshow-new-wrapper');
        slideshows.forEach((slideshow) => {
          new SlideshowNew(slideshow);
        });

        // Re-initialize How We Help (New)
        const howWeHelpNewBlocks = section.querySelectorAll('.how-we-help-new');
        howWeHelpNewBlocks.forEach((block) => {
          new HowWeHelpNew(block);
        });

        // Re-initialize How We Help (Legacy)
        if (section.classList.contains('how-we-help-section') || section.querySelector('.how-we-help-section')) {
          const howWeHelpSection = /** @type {HTMLElement | null} */ (section.classList.contains('how-we-help-section') ? section : section.querySelector('.how-we-help-section'));
          if (howWeHelpSection && (/** @type {any} */ (window))['HowWeHelpSection']) {
            new ((/** @type {any} */ (window))['HowWeHelpSection'])(event.detail.sectionId);
          }
        }

        // Re-initialize Collection Tabs
        const collectionTabsSections = section.querySelectorAll('.collection-tabs-section[data-block-id]');
        collectionTabsSections.forEach((tabsSection) => {
          const blockId = tabsSection.dataset.blockId;
          new CollectionTabsSection(blockId);
        });

        // Re-initialize Join Movement
        if (section.classList.contains('join-movement-section') || section.querySelector('.join-movement-section')) {
          const joinMovementSection = section.classList.contains('join-movement-section') ? section : section.querySelector('.join-movement-section');
          if (joinMovementSection) {
            const sectionId = /** @type {HTMLElement} */ (joinMovementSection).dataset.sectionId;
            if (sectionId) {
              new JoinMovementSection(sectionId);
            }
          }
        }

        // Re-initialize Bundle Card Sliders
        const bundleCardContainers = section.querySelectorAll('.bundle-card-section__container');
        bundleCardContainers.forEach((containerEL) => {
          const container = /** @type {HTMLElement} */ (containerEL);
          new BundleCardSlider(container);
        });
      }
    }
  });
}

// Listen for slideshow select events to update dots
document.addEventListener('slideshow:select', (/** @type {any} */ event) => {
  const slideshowComponent = event.target.closest('slideshow-component');
  if (slideshowComponent) {
    const currentIndex = event.detail?.index ?? 0;
    const dots = slideshowComponent.querySelectorAll('.custom-slideshow__dot');
    dots.forEach((/** @type {HTMLElement} */ dot, /** @type {number} */ index) => {
      dot.setAttribute('aria-selected', (index === currentIndex).toString());
    });
  }
});

// Bundle Card Description Toggle
function initBundleCardDescriptions() {
  const toggles = document.querySelectorAll('[data-bundle-description-toggle]');
  toggles.forEach((toggle) => {
    toggle.addEventListener('click', function () {
      const blockId = this.dataset.bundleDescriptionToggle;
      const content = document.getElementById(`bundle-description-${blockId}`);
      const isExpanded = this.getAttribute('aria-expanded') === 'true';

      if (content) {
        if (isExpanded) {
          content.setAttribute('hidden', '');
          this.setAttribute('aria-expanded', 'false');
        } else {
          content.removeAttribute('hidden');
          this.setAttribute('aria-expanded', 'true');
        }
      }
    });
  });
}

// Bundle Card Add to Cart
function initBundleCardAddToCart() {
  const addToCartButtons = document.querySelectorAll('[data-bundle-add-to-cart]');
  addToCartButtons.forEach((button) => {
    button.addEventListener('click', async function (e) {
      e.preventDefault();
      e.stopPropagation();

      const target = /** @type {HTMLElement} */ (e.currentTarget);
      const bundleItemsStr = target.dataset.bundleItems;
      const productId = target.dataset.productId;
      const variantId = target.dataset.variantId;

      let items = [];
      if (bundleItemsStr) {
        try {
          items = JSON.parse(bundleItemsStr);
        } catch (err) {
          console.error('Invalid bundle items JSON', err);
        }
      } else if (variantId) {
        items = [{
          id: variantId,
          quantity: 1
        }];
      }

      if (items.length === 0) {
        console.error('No items to add');
        alert('Please select products for this bundle');
        return;
      }      // Disable button during request
      target.style.pointerEvents = 'none';
      const originalText = target.textContent;
      target.textContent = 'Adding...';

      try {
        const response = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({ 
            items: items,
            sections: 'header' // Request the header section which contains the cart drawer
          })
        });

        const data = await response.json();

        if (!response.ok) {
          throw new Error(data.description || 'Failed to add to cart');
        }

        // Refresh cart drawer content using returned section HTML
        if (data.sections && data.sections['header']) {
          const html = data.sections['header'];
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, 'text/html');
          
          // Find the new cart drawer content
          const newCartDrawerContent = doc.querySelector('cart-drawer-component');
          const currentCartDrawer = document.querySelector('cart-drawer-component');
          
          if (newCartDrawerContent && currentCartDrawer) {
            // Replace the internal content of the drawer to refresh the list and totals
            const innerContent = currentCartDrawer.querySelector('.cart-drawer__inner');
            const newInnerContent = newCartDrawerContent.querySelector('.cart-drawer__inner');
            
            if (innerContent && newInnerContent) {
              innerContent.innerHTML = newInnerContent.innerHTML;
            } else {
              // Fallback to replacing entire inner content if class names mismatch
              currentCartDrawer.innerHTML = newCartDrawerContent.innerHTML;
            }
          }
        } else {
          // Fallback: Fetch cart.js if section rendering failed or returned no data
          await fetch('/cart.js');
        }

        // Success - update button text temporarily
        target.textContent = 'Added!';

        // Dispatch cart update event to notify other components (bubbles, etc)
        if (window.dispatchEvent) {
          const cartUpdateEvent = new CustomEvent('cart:update', { 
            bubbles: true,
            detail: {
              data: {
                source: 'bundle-add',
                sections: data.sections
              }
            }
          });
          window.dispatchEvent(cartUpdateEvent);
        }

        // Open cart drawer AFTER DOM update
        try {
          const cartDrawer = /** @type {any} */ (document.querySelector('cart-drawer-component'));
          if (cartDrawer && typeof cartDrawer.open === 'function') {
            cartDrawer.open();
          }
        } catch (drawerErr) {
          console.error('Error opening cart drawer:', drawerErr);
        }

        // Show toast as secondary feedback
        const toast = document.getElementById('ls-atc-toast');
        const titleElement = document.getElementById('toast-product-title');
        if (toast && titleElement) {
          titleElement.textContent = 'Bundle added to cart';
          toast.classList.add('is-active');
          setTimeout(() => {
            toast.classList.remove('is-active');
          }, 3000);
        }

        // Reset button after 2 seconds
        setTimeout(() => {
          target.textContent = originalText;
          target.style.pointerEvents = '';
        }, 2000);

      } catch (error) {
        console.error('Error adding to cart:', error);
        alert(error.message || 'Something went wrong. Please try again.');
        target.textContent = 'Error - Try Again';
        setTimeout(() => {
          target.textContent = originalText;
          target.style.pointerEvents = '';
        }, 2000);
      }
    });
  });
}

// Bundle Card Slider for Mobile - DISABLED on mobile, vertical layout instead
class BundleCardSlider {
  /**
   * @param {HTMLElement} container
   */
  constructor(container) {
    this.container = container;
    this.track = container.querySelector('.ls-bundle-track');
    this.cards = container.querySelectorAll('.bundle-card.ls-bundle-card, .ls-bundle-card');

    // On mobile: disable all JavaScript transform behavior
    if (window.innerWidth <= 749) {
      // Reset any transforms - use vertical CSS layout
      if (this.track) {
        this.track.style.transform = '';
        this.track.style.transition = '';
      }
      if (this.container) {
        this.container.style.transform = '';
        this.container.style.transition = '';
      }
      return; // Exit early - no JavaScript needed on mobile
    }

    // Desktop: initialize if needed (but track should use grid, not transform)
    if (this.track) {
      this.track.style.transform = '';
      this.track.style.transition = '';
    }
  }

  init() {
    // Disabled on mobile - vertical layout via CSS
    if (window.innerWidth <= 749) {
      return;
    }
  }

  updatePosition() {
    // Disabled
  }

  setupTouchEvents() {
    // Disabled
  }

  getCurrentTranslateX() {
    return 0;
  }

  setupResize() {
    // Disabled
  }
}

// Initialize bundle card functionality
document.addEventListener('DOMContentLoaded', () => {
  initBundleCardDescriptions();
  initBundleCardAddToCart();

  // Initialize Bundle Card Sliders
  const bundleCardContainers = document.querySelectorAll('.bundle-card-section__container');
  bundleCardContainers.forEach((container) => {
    new BundleCardSlider(container);
  });
});

// Re-initialize in Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (/** @type {any} */ event) => {
    initBundleCardDescriptions();
    initBundleCardAddToCart();

    // Re-initialize Bundle Card Sliders
    const bundleCardContainers = event.detail.sectionId ? document.querySelector(`[data-section-id="${event.detail.sectionId}"]`)?.querySelectorAll('.bundle-card-section__container') || [] : [];
    bundleCardContainers.forEach((container) => {
      new BundleCardSlider(container);
    });
  });
}

// Featured Quotes Slider Functionality
class FeaturedQuotesSlider {
  constructor(element) {
    /** @type {HTMLElement} */
    this.slider = element;
    /** @type {HTMLElement | null} */
    this.track = element.querySelector('[data-slider-track]');
    /** @type {NodeListOf<HTMLElement>} */
    this.cards = element.querySelectorAll('.featured-quote-card');
    /** @type {number} */
    this.currentIndex = 0;
    /** @type {boolean} */
    this.autoplay = element.dataset.autoplay === 'true';
    /** @type {number} */
    this.autoplaySpeed = parseInt(element.dataset.autoplaySpeed || '5000') || 5000;
    /** @type {any} */
    this.autoplayInterval = null;
    /** @type {boolean} */
    this.isTransitioning = false;
    /** @type {number} */
    this.totalSlides = 0;
    /** @type {number} */
    this.slidesPerView = 3;
    /** @type {number} */
    this.cardsDesktop = parseInt(element.dataset.cardsDesktop || '3') || 3;
    /** @type {number} */
    this.cardsTablet = parseInt(element.dataset.cardsTablet || '2') || 2;
    /** @type {number} */
    this.cardsMobile = parseInt(element.dataset.cardsMobile || '1') || 1;

    this.init();
  }

  init() {
    if (this.cards.length === 0) return;

    // Calculate slides per view based on screen size
    this.updateSlidesPerView();

    // Setup touch/swipe support
    this.setupTouchEvents();

    // Setup autoplay
    if (this.autoplay && this.cards.length > this.slidesPerView) {
      this.startAutoplay();
    }

    // Handle window resize
    let resizeTimeout;
    window.addEventListener('resize', () => {
      if (resizeTimeout) clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.updateSlidesPerView();
        this.goToSlide(this.currentIndex);
      }, 250);
    });
  }

  updateSlidesPerView() {
    const width = window.innerWidth;
    if (width <= 749) {
      this.slidesPerView = this.cardsMobile;
    } else if (width <= 989) {
      this.slidesPerView = this.cardsTablet;
    } else {
      this.slidesPerView = this.cardsDesktop;
    }

    /** @type {HTMLElement[]} */
    const cards = Array.from(this.cards || []);
    const cardsLength = cards.length;
    this.totalSlides = Math.max(1, cardsLength - this.slidesPerView + 1);

    // Ensure current index is valid
    if (this.currentIndex >= this.totalSlides) {
      this.currentIndex = Math.max(0, this.totalSlides - 1);
    }
  }

  /** @param {number} index */
  goToSlide(index) {
    if (this.isTransitioning || this.totalSlides === 0 || this.totalSlides === undefined) return;

    this.currentIndex = Math.max(0, Math.min(index, this.totalSlides - 1));
    this.isTransitioning = true;

    // Calculate translateX
    if (this.cards.length === 0 || !this.track) return;
    const firstCard = /** @type {HTMLElement} */ (this.cards[0]);
    const cardWidth = firstCard.offsetWidth;
    const gap = parseFloat(getComputedStyle(this.track).gap) || 32;
    const translateX = -(this.currentIndex * (cardWidth + gap));

    this.track.style.transform = `translateX(${translateX}px)`;

    // Update dots
    this.updateDots();

    // Reset transition flag
    setTimeout(() => {
      this.isTransitioning = false;
    }, 600);

    // Always restart autoplay to keep it continuous
    if (this.autoplay) {
      this.stopAutoplay();
      this.startAutoplay();
    }
  }

  next() {
    if (this.totalSlides === undefined) return;
    const nextIndex = (this.currentIndex + 1) % this.totalSlides;
    this.goToSlide(nextIndex);
  }

  prev() {
    if (this.totalSlides === undefined) return;
    const prevIndex = (this.currentIndex - 1 + this.totalSlides) % this.totalSlides;
    this.goToSlide(prevIndex);
  }

  updateDots() {
    // Dots removed - no longer needed
  }

  startAutoplay() {
    if (!this.cards || this.cards.length <= this.slidesPerView) return;

    this.stopAutoplay();
    this.autoplayInterval = setInterval(() => {
      this.next();
    }, this.autoplaySpeed);
  }

  stopAutoplay() {
    if (this.autoplayInterval) {
      clearInterval(this.autoplayInterval);
      this.autoplayInterval = null;
    }
  }

  setupTouchEvents() {
    if (!this.track) return;
    let startX = 0;
    let currentX = 0;
    let isDragging = false;
    let startTranslateX = 0;

    this.track.addEventListener('touchstart', (/** @type {TouchEvent} */ e) => {
      if (e.touches.length === 0) return;
      startX = e.touches[0].clientX;
      startTranslateX = this.getCurrentTranslateX();
      isDragging = true;
      // Don't stop autoplay, just pause temporarily
    });

    this.track.addEventListener('touchmove', (/** @type {TouchEvent} */ e) => {
      if (!isDragging || e.touches.length === 0) return;
      currentX = e.touches[0].clientX;
      const diff = currentX - startX;
      const newTranslateX = startTranslateX + diff;
      this.track.style.transform = `translateX(${newTranslateX}px)`;
      this.track.style.transition = 'none';
    });

    this.track.addEventListener('touchend', () => {
      if (!isDragging || !this.track) return;
      isDragging = false;
      this.track.style.transition = '';

      const diff = currentX - startX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.prev();
        } else {
          this.next();
        }
      } else {
        this.goToSlide(this.currentIndex);
      }

      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    });

    // Mouse drag support for desktop
    let mouseStartX = 0;
    let mouseCurrentX = 0;
    let isMouseDragging = false;
    let mouseStartTranslateX = 0;

    this.track.addEventListener('mousedown', (/** @type {MouseEvent} */ e) => {
      mouseStartX = e.clientX;
      mouseStartTranslateX = this.getCurrentTranslateX();
      isMouseDragging = true;
      // Don't stop autoplay, just pause temporarily
      e.preventDefault();
    });

    document.addEventListener('mousemove', (/** @type {MouseEvent} */ e) => {
      if (!isMouseDragging || !this.track) return;
      mouseCurrentX = e.clientX;
      const diff = mouseCurrentX - mouseStartX;
      const newTranslateX = mouseStartTranslateX + diff;
      this.track.style.transform = `translateX(${newTranslateX}px)`;
      this.track.style.transition = 'none';
    });

    document.addEventListener('mouseup', () => {
      if (!isMouseDragging || !this.track) return;
      isMouseDragging = false;
      this.track.style.transition = '';

      const diff = mouseCurrentX - mouseStartX;
      const threshold = 50;

      if (Math.abs(diff) > threshold) {
        if (diff > 0) {
          this.prev();
        } else {
          this.next();
        }
      } else {
        this.goToSlide(this.currentIndex);
      }

      // Always restart autoplay after mouse drag
      if (this.autoplay) {
        this.stopAutoplay();
        this.startAutoplay();
      }
    });
  }

  getCurrentTranslateX() {
    if (!this.track) return 0;
    const transform = getComputedStyle(this.track).transform;
    if (transform === 'none') return 0;
    const matrix = transform.match(/matrix.*\((.+)\)/);
    return matrix ? parseFloat(matrix[1].split(', ')[4]) : 0;
  }
}

// Initialize Featured Quotes Sliders
function initFeaturedQuotesSliders() {
  const sliders = document.querySelectorAll('.featured-quotes-slider');
  sliders.forEach((slider) => {
    new FeaturedQuotesSlider(slider);
  });
}

// How We Help V2 - Hard Reset
(function () {
  const root = document.querySelector('.howwehelp-v2');
  if (!root) return;

  const track = root.querySelector('[data-howwehelp-track]');
  const slides = Array.from(root.querySelectorAll('[data-howwehelp-slide]'));
  const dotsWrap = root.querySelector('[data-howwehelp-dots]');
  const dots = Array.from(root.querySelectorAll('[data-howwehelp-dot]'));

  if (!track || !(track instanceof HTMLElement) || slides.length === 0) return;

  // Hard kill click blockers if any old wrappers survived
  // Ensure slides are not wrapped by anchors via CSS pointer events:
  const clickBlockers = root.querySelectorAll('.howwehelp-v2 a');
  clickBlockers.forEach((/** @type {Element} */ aEL) => {
    const a = /** @type {HTMLElement} */ (aEL);
    // If an old template wrapped the whole card in <a>, it will steal clicks.
    // Keep links inside content clickable, but prevent full-card overlays from hijacking.
    const slide = a.closest('.howwehelp-v2__slide');
    if (slide && a.parentElement === slide) {
      a.style.pointerEvents = 'none';
    }
  });

  /** @param {number} index */
  function setActiveDot(index) {
    dots.forEach((d, i) => d.classList.toggle('is-active', i === index));
  }

  function getActiveIndex() {
    if (!track) return 0; // Add null guard for track
    const trackRect = track.getBoundingClientRect();
    const centerX = trackRect.left + trackRect.width / 2;

    let bestIdx = 0;
    let bestDist = Infinity;

    slides.forEach((sEL, idx) => {
      const s = /** @type {HTMLElement} */ (sEL);
      const r = s.getBoundingClientRect();
      const slideCenter = r.left + r.width / 2;
      const dist = Math.abs(centerX - slideCenter);
      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = idx;
      }
    });

    return bestIdx;
  }

  // Toggle accordion only via arrow button
  root.addEventListener('click', (/** @type {any} */ e) => {
    const btn = e.target instanceof HTMLElement ? e.target.closest('.howwehelp-v2__toggle') : null;
    if (!btn) return;

    e.preventDefault();
    e.stopPropagation();

    const slide = btn.closest('.howwehelp-v2__slide');
    if (!slide) return;
    const content = slide.querySelector('.howwehelp-v2__content');
    if (!(content instanceof HTMLElement)) return;

    const isOpen = slide.classList.toggle('is-open');
    btn.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

    if (isOpen) {
      content.hidden = false;
    } else {
      content.hidden = true;
    }
  }, true);

  // Update dots on scroll
  let raf = null;
  track.addEventListener('scroll', () => {
    if (raf) cancelAnimationFrame(raf);
    raf = requestAnimationFrame(() => setActiveDot(getActiveIndex()));
  }, { passive: true });

  // Dot click -> scroll into view
  dots.forEach(dot => {
    dot.addEventListener('click', (/** @type {Event} */ e) => {
      e.preventDefault();
      e.stopPropagation();
      const idx = Number(dot.getAttribute('data-howwehelp-dot'));
      const target = slides[idx];
      if (target instanceof HTMLElement) target.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
      setActiveDot(idx);
    }, true);
  });

  // Initial
  setActiveDot(0);
})();

// Restore initialization for other sliders
document.addEventListener('DOMContentLoaded', () => {
  if (typeof initFeaturedQuotesSliders === 'function') {
    initFeaturedQuotesSliders();
  }
});

// Re-initialize in Shopify theme editor
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    if (typeof initFeaturedQuotesSliders === 'function') {
      initFeaturedQuotesSliders();
    }
  });
  document.addEventListener('shopify:block:select', () => {
    if (typeof initFeaturedQuotesSliders === 'function') {
      initFeaturedQuotesSliders();
    }
  });
}


/*
  HowWeHelpNew
  Manages tab switching for the new How We Help block.
*/

const AUTOPLAY_MS = 5000;

class SlideshowNew {
  constructor(element) {
    this.element = element;
    this.scrollEl = element.querySelector('.slideshow-new');
    this.track = element.querySelector('.slideshow-new__track');
    this.slides = element.querySelectorAll('.slideshow-new__slide');
    this.dots = element.querySelectorAll('.ss-dot');
    this.duration = AUTOPLAY_MS;
    this.currentIndex = 0;
    this.totalSlides = this.slides.length;

    this.startTime = null;
    this.elapsedTime = 0;
    this.isPaused = false;
    this.animationId = null;

    this.init();
  }

  init() {
    if (this.totalSlides === 0) return;

    this.dots.forEach((dot, index) => {
      dot.addEventListener('click', () => {
        this.goToSlide(index);
      });
    });

    this.element.addEventListener('mouseenter', () => this.pause());
    this.element.addEventListener('mouseleave', () => this.resume());

    // Sync current index if user swipes manually
    let scrollTimeout;
    if (this.scrollEl) {
      this.scrollEl.addEventListener('scroll', () => {
        if (scrollTimeout) clearTimeout(scrollTimeout);
        scrollTimeout = setTimeout(() => {
          if (!this.scrollEl) return;
          const index = Math.round(this.scrollEl.scrollLeft / this.scrollEl.offsetWidth);
          if (index !== this.currentIndex && index >= 0 && index < this.totalSlides) {
            this.currentIndex = index;
            this.updateClasses();
            this.resetDotProgress();
          }
        }, 100);
      }, { passive: true });
    }

    this.updateClasses();
    this.startDotProgress();
  }

  startDotProgress() {
    if (this.animationId !== null) cancelAnimationFrame(this.animationId);

    // Resume from where we left off (elapsedTime)
    this.startTime = performance.now() - this.elapsedTime;

    /** @param {number} time */
    const animate = (time) => {
      if (this.isPaused || this.startTime === null) return;

      this.elapsedTime = time - this.startTime;
      const progress = Math.min(this.elapsedTime / this.duration, 1);

      this.updateProgressUI(progress);

      if (progress >= 1) {
        this.next();
      } else {
        this.animationId = requestAnimationFrame(animate);
      }
    };
    this.animationId = requestAnimationFrame(animate);
  }

  /** @param {number} progress */
  updateProgressUI(progress) {
    if (this.currentIndex === null) return;
    const activeDot = this.dots[this.currentIndex];
    if (!activeDot) return;

    const fill = activeDot.querySelector('.ss-dot-progress');
    if (fill instanceof SVGElement) {
      // Circumference for r=10 is 2 * PI * 10 = 62.83
      const circumference = 62.83;
      const offset = circumference * (1 - progress);
      fill.style.strokeDashoffset = offset.toFixed(2);
    }
  }

  resetDotProgress() {
    if (this.animationId !== null) cancelAnimationFrame(this.animationId);
    this.elapsedTime = 0;

    // Reset all dots UI
    this.dots.forEach((/** @type {Element} */ dot) => {
      const fill = dot.querySelector('.ss-dot-progress');
      if (fill instanceof SVGElement) {
        fill.style.strokeDashoffset = '62.83';
      }
    });

    if (!this.isPaused) {
      this.startDotProgress();
    }
  }

  pause() {
    this.isPaused = true;
    if (this.animationId !== null) cancelAnimationFrame(this.animationId);
  }

  resume() {
    if (!this.isPaused) return;
    this.isPaused = false;
    this.startDotProgress();
  }

  /** @param {number} index */
  goToSlide(index) {
    if (index < 0) index = this.totalSlides - 1;
    if (index >= this.totalSlides) index = 0;

    this.currentIndex = index;
    if (this.scrollEl) {
      this.scrollEl.scrollTo({
        left: index * this.scrollEl.offsetWidth,
        behavior: 'smooth'
      });
    }

    this.updateClasses();
    this.resetDotProgress();
  }

  next() {
    this.goToSlide(this.currentIndex + 1);
  }

  updateClasses() {
    this.slides.forEach((/** @type {HTMLElement} */ slide, /** @type {number} */ i) => slide.classList.toggle('active', i === this.currentIndex));
    this.dots.forEach((/** @type {HTMLElement} */ dot, /** @type {number} */ i) => dot.classList.toggle('active', i === this.currentIndex));
  }
}

class HowWeHelpNew {
  constructor(element) {
    this.root = element;
    this.navButtons = this.root.querySelectorAll('.how-we-help-new__tab-button');
    this.panels = this.root.querySelectorAll('.how-we-help-new__panel');

    this.init();
  }

  init() {
    this.navButtons.forEach((/** @type {HTMLElement} */ btn) => {
      btn.addEventListener('click', (/** @type {Event} */ e) => {
        e.preventDefault();
        const index = btn.dataset.index;
        if (index !== undefined) {
          this.switchTab(index, btn);
        }
      });
    });
  }

  /** 
   * @param {string} index 
   * @param {HTMLElement} activeBtn 
   */
  switchTab(index, activeBtn) {
    // Update Buttons
    this.navButtons.forEach((/** @type {HTMLElement} */ btn) => {
      const isActive = btn === activeBtn;
      btn.classList.toggle('is-active', isActive);
      btn.setAttribute('aria-selected', isActive.toString());
    });

    // Update Panels
    this.panels.forEach((/** @type {HTMLElement} */ panel) => {
      // Panel ID format: hwh-panel-{blockId}-{index}
      // We check if the panel's ID ends with the index
      const panelId = panel.id;
      const panelIndex = panelId.split('-').pop(); // Get last part

      const isActive = panelIndex === index;

      if (isActive) {
        panel.removeAttribute('hidden');
        panel.classList.add('is-active');
      } else {
        panel.setAttribute('hidden', '');
        panel.classList.remove('is-active');
      }
    });
  }
}

class BundleSlider {
  constructor(element) {
    this.slider = element;
    this.section = element.closest('.bundle-card-section');
    if (!this.section) return;

    this.dotsContainer = this.section.querySelector('.bundle-card-section__slider-dots');
    this.dots = this.dotsContainer ? this.dotsContainer.querySelectorAll('.bundle-card-section__slider-dot') : [];
    this.cards = this.slider.querySelectorAll('.ls-bundle-card');

    if (this.dots.length === 0) return;

    this.init();
  }

  init() {
    this.slider.addEventListener('scroll', () => {
      requestAnimationFrame(() => this.updateDots());
    }, { passive: true });

    // Click on dots to scroll
    this.dots.forEach((/** @type {HTMLElement} */ dot, index) => {
      dot.addEventListener('click', () => {
        const targetCard = this.cards[index];
        if (targetCard instanceof HTMLElement) {
          targetCard.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'start' });
        }
      });
    });

    // Initial update
    this.updateDots();
  }

  updateDots() {
    if (!this.slider || this.dots.length === 0) return;

    const scrollLeft = this.slider.scrollLeft;
    const width = this.slider.offsetWidth;

    // Use card width for more precise calculation if cards are smaller than container
    const cardFirst = this.cards[0];
    const cardWidth = cardFirst ? cardFirst.offsetWidth : width;

    // Calculate index based on scroll position - simplified for 3 items
    const index = Math.round(scrollLeft / (cardWidth || 1));

    this.dots.forEach((/** @type {HTMLElement} */ dot, /** @type {number} */ i) => {
      dot.classList.toggle('bundle-card-section__slider-dot--active', i === index);
    });
  }
}

} // end __slideshowCustomLoaded guard
